import { Outlet, createRootRoute, createRoute, createRouter, redirect } from '@tanstack/react-router'
import { Landing } from '@/components/landing/Landing'
import { NotFound } from '@/components/shell/Loading'
import { ThemeProvider } from '@/providers/ThemeProvider'
import { AdminPage } from './routes/admin'
import { CanvasPage, InventoryPage, LibraryPage, PowerIndexPage, PowerPlanPage, ProjectsPage, RunSheetPage, SettingsPage, SharePage } from './routes/pages'
import { ForgotPasswordPage, HomePage, InvitePage, LoginPage, OnboardingPage, PublicLayout, RegisterPage, ResetPasswordPage } from './routes/public'
import { WorkspaceLayout } from './routes/workspace'

const rootRoute = createRootRoute({
  component: () => (
    <ThemeProvider>
      <Outlet />
    </ThemeProvider>
  ),
  notFoundComponent: NotFound,
})

const home = createRoute({ getParentRoute: () => rootRoute, path: '/', component: () => <HomePage Landing={Landing} /> })

const publicLayout = createRoute({ getParentRoute: () => rootRoute, id: 'public', component: PublicLayout })
const login = createRoute({ getParentRoute: () => publicLayout, path: '/login', component: LoginPage })
const register = createRoute({ getParentRoute: () => publicLayout, path: '/register', component: RegisterPage })
const forgot = createRoute({ getParentRoute: () => publicLayout, path: '/forgot-password', component: ForgotPasswordPage })
const reset = createRoute({ getParentRoute: () => publicLayout, path: '/reset-password', component: ResetPasswordPage })
const onboarding = createRoute({ getParentRoute: () => publicLayout, path: '/onboarding', component: OnboardingPage })
const invite = createRoute({
  getParentRoute: () => publicLayout,
  path: '/invite/$token',
  component: function Invite() {
    const { token } = invite.useParams()
    return <InvitePage token={token} />
  },
})

const share = createRoute({
  getParentRoute: () => rootRoute,
  path: '/share/$token',
  component: function Share() {
    const { token } = share.useParams()
    return <SharePage token={token} />
  },
})

const admin = createRoute({ getParentRoute: () => rootRoute, path: '/admin', component: AdminPage })

const workspace = createRoute({
  getParentRoute: () => rootRoute,
  path: '/w/$slug',
  component: function Workspace() {
    const { slug } = workspace.useParams()
    return <WorkspaceLayout slug={slug} />
  },
})
const wsIndex = createRoute({ getParentRoute: () => workspace, path: '/', component: () => null, beforeLoad: ({ params }) => { throw redirectTo(`/w/${(params as { slug: string }).slug}/projects`) } })
const projects = createRoute({ getParentRoute: () => workspace, path: '/projects', component: function P() { return <ProjectsPage slug={workspace.useParams().slug} /> } })
const canvas = createRoute({ getParentRoute: () => workspace, path: '/projects/$id', component: function C() { const p = canvas.useParams(); return <CanvasPage slug={p.slug} id={p.id} /> } })
const runSheet = createRoute({ getParentRoute: () => workspace, path: '/projects/$id/run-sheet', component: function R() { const p = runSheet.useParams(); return <RunSheetPage slug={p.slug} id={p.id} /> } })
const library = createRoute({ getParentRoute: () => workspace, path: '/library', component: function L() { return <LibraryPage slug={workspace.useParams().slug} /> } })
const inventory = createRoute({ getParentRoute: () => workspace, path: '/inventory', component: function I() { return <InventoryPage slug={workspace.useParams().slug} /> } })
const power = createRoute({ getParentRoute: () => workspace, path: '/power', component: function Pw() { return <PowerIndexPage slug={workspace.useParams().slug} /> } })
const powerPlan = createRoute({ getParentRoute: () => workspace, path: '/power/$id', component: function Pp() { const p = powerPlan.useParams(); return <PowerPlanPage slug={p.slug} id={p.id} /> } })
const settings = createRoute({ getParentRoute: () => workspace, path: '/settings', component: function S() { return <SettingsPage slug={workspace.useParams().slug} /> } })

function redirectTo(href: string) {
  return redirect({ to: href as never })
}

export const router = createRouter({
  routeTree: rootRoute.addChildren([
    home,
    publicLayout.addChildren([login, register, forgot, reset, onboarding, invite]),
    share,
    admin,
    workspace.addChildren([wsIndex, projects, canvas, runSheet, library, inventory, power, powerPlan, settings]),
  ]),
  defaultNotFoundComponent: NotFound,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
